import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { QRCodeSVG } from "qrcode.react";
import { Loader2, ShieldCheck, ShieldAlert, Copy, KeyRound, Download } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Card, CardHeader, CardTitle, CardContent, Button, Input, Label, StatusBadge,
  LoadingBlock, ErrorState, toast,
} from "@/components/ds";

function BackupCodes({ codes, onAcknowledge }: { codes: string[]; onAcknowledge?: () => void }) {
  const [saved, setSaved] = useState(false);

  const download = () => {
    const body = [
      "ResFil AI — two-factor backup codes",
      "Each code can be used once if you lose your authenticator app.",
      "Keep this somewhere safe, and separate from your phone.",
      "",
      ...codes,
    ].join("\n");
    const url = URL.createObjectURL(new Blob([body], { type: "text/plain" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "resfil-backup-codes.txt";
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Backup codes downloaded");
  };

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 p-4" data-testid="mfa-backup-codes">
      <p className="mb-2 flex items-center gap-2 text-sm font-semibold text-amber-900">
        <KeyRound className="h-4 w-4" /> Save your backup codes
      </p>
      <p className="mb-3 text-xs text-amber-800">
        Each code works once if you lose your authenticator. They are shown only now and cannot be
        retrieved later — store them somewhere other than your phone.
      </p>
      <div className="grid grid-cols-2 gap-2 font-mono text-sm text-slate-800">
        {codes.map((c) => (<span key={c} className="rounded bg-white px-2 py-1 text-center">{c}</span>))}
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <Button variant="ghost" size="sm" data-testid="mfa-copy-backup"
          onClick={() => { navigator.clipboard.writeText(codes.join("\n")); toast.success("Backup codes copied"); }}>
          <Copy className="h-4 w-4" /> Copy all
        </Button>
        <Button variant="ghost" size="sm" data-testid="mfa-download-backup" onClick={download}>
          <Download className="h-4 w-4" /> Download
        </Button>
      </div>
      {onAcknowledge && (
        <div className="mt-4 border-t border-amber-200 pt-3">
          <label className="flex cursor-pointer items-start gap-2 text-xs text-amber-900">
            <input type="checkbox" className="mt-0.5" data-testid="mfa-backup-ack"
              checked={saved} onChange={(e) => setSaved(e.target.checked)} />
            <span>I have saved these backup codes somewhere safe.</span>
          </label>
          <Button className="mt-3 w-full" data-testid="mfa-backup-continue"
            disabled={!saved} onClick={onAcknowledge}>
            Continue
          </Button>
        </div>
      )}
    </div>
  );
}

export function MfaManager({ onEnabled }: { onEnabled?: () => void }) {
  const qc = useQueryClient();
  const { refresh } = useAuth();
  const { data: status, isLoading, isError, refetch } = useQuery({ queryKey: ["mfa-status"], queryFn: api.mfaStatus });

  const [setupData, setSetupData] = useState<{ secret: string; otpauth_uri: string } | null>(null);
  const [code, setCode] = useState("");
  const [disableCode, setDisableCode] = useState("");
  const [regenCode, setRegenCode] = useState("");
  const [newCodes, setNewCodes] = useState<string[] | null>(null);

  const startSetup = useMutation({
    mutationFn: api.mfaSetup,
    onSuccess: (d) => setSetupData(d),
    onError: (e) => toast.error(formatApiError(e)),
  });
  const enable = useMutation({
    mutationFn: () => api.mfaEnable(code),
    onSuccess: async (d) => {
      setNewCodes(d.backup_codes); setSetupData(null); setCode("");
      await refresh(); qc.invalidateQueries({ queryKey: ["mfa-status"] });
      toast.success("Two-factor authentication enabled");
      // Intentionally NOT calling onEnabled() here. The backup codes are displayed only once,
      // and navigating away immediately means the user never sees them.
    },
    onError: (e) => toast.error(formatApiError(e)),
  });
  const disable = useMutation({
    mutationFn: () => api.mfaDisable(disableCode),
    onSuccess: async () => {
      setDisableCode(""); setNewCodes(null);
      await refresh(); qc.invalidateQueries({ queryKey: ["mfa-status"] });
      toast.success("Two-factor authentication turned off");
    },
    onError: (e) => toast.error(formatApiError(e)),
  });
  const regen = useMutation({
    mutationFn: () => api.mfaRegenerateBackupCodes(regenCode),
    onSuccess: async (d) => {
      setNewCodes(d.backup_codes); setRegenCode("");
      qc.invalidateQueries({ queryKey: ["mfa-status"] });
      toast.success("New backup codes generated");
    },
    onError: (e) => toast.error(formatApiError(e)),
  });

  if (isLoading) return <LoadingBlock rows={3} />;
  if (isError || !status) return <ErrorState onRetry={refetch} />;

  return (
    <Card data-testid="mfa-manager">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          {status.enabled ? <ShieldCheck className="h-5 w-5 text-emerald-600" /> : <ShieldAlert className="h-5 w-5 text-amber-600" />}
          Two-factor authentication
        </CardTitle>
        <StatusBadge tone={status.enabled ? "green" : status.required ? "amber" : "slate"} data-testid="mfa-badge">
          {status.enabled ? "Enabled" : status.required ? "Required" : "Off"}
        </StatusBadge>
      </CardHeader>
      <CardContent className="space-y-4">
        {status.required && !status.enabled && (
          <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800" data-testid="mfa-required-note">
            Two-factor authentication is required for your role. Please set it up to secure your account.
          </p>
        )}

        {/* Enabled state */}
        {status.enabled && !newCodes && (
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Your account is protected with an authenticator app. Backup codes remaining:{" "}
              <span className="font-semibold text-slate-900" data-testid="mfa-backup-remaining">{status.backup_codes_remaining}</span>.
            </p>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="rounded-lg border border-slate-200 p-3">
                <Label>Regenerate backup codes</Label>
                <p className="mb-2 text-xs text-slate-500">Enter a current authenticator code.</p>
                <div className="flex gap-2">
                  <Input data-testid="mfa-regen-code" inputMode="numeric" placeholder="123456" value={regenCode} onChange={(e) => setRegenCode(e.target.value)} />
                  <Button variant="secondary" data-testid="mfa-regen-btn" disabled={regenCode.length < 6 || regen.isPending} onClick={() => regen.mutate()}>
                    {regen.isPending && <Loader2 className="h-4 w-4 animate-spin" />} Generate
                  </Button>
                </div>
              </div>
              {!status.required && (
                <div className="rounded-lg border border-red-200 p-3">
                  <Label>Turn off two-factor</Label>
                  <p className="mb-2 text-xs text-slate-500">Enter a current authenticator code.</p>
                  <div className="flex gap-2">
                    <Input data-testid="mfa-disable-code" inputMode="numeric" placeholder="123456" value={disableCode} onChange={(e) => setDisableCode(e.target.value)} />
                    <Button variant="danger" data-testid="mfa-disable-btn" disabled={disableCode.length < 6 || disable.isPending} onClick={() => disable.mutate()}>
                      {disable.isPending && <Loader2 className="h-4 w-4 animate-spin" />} Turn off
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {status.enabled && newCodes && <BackupCodes codes={newCodes} onAcknowledge={onEnabled} />}

        {/* Not enabled — setup wizard */}
        {!status.enabled && !setupData && !newCodes && (
          <Button data-testid="mfa-setup-btn" disabled={startSetup.isPending} onClick={() => startSetup.mutate()}>
            {startSetup.isPending && <Loader2 className="h-4 w-4 animate-spin" />} Set up two-factor authentication
          </Button>
        )}

        {!status.enabled && setupData && (
          <div className="space-y-4" data-testid="mfa-setup-panel">
            <p className="text-sm text-slate-600">
              1. Scan this QR code with Google Authenticator, 1Password, Authy or a similar app.
            </p>
            <div className="flex flex-col items-center gap-3 rounded-lg border border-slate-200 bg-white p-4">
              <QRCodeSVG value={setupData.otpauth_uri} size={168} data-testid="mfa-qr" />
              <div className="text-center">
                <p className="text-xs text-slate-500">Or enter this key manually:</p>
                <code className="text-sm font-medium text-slate-800" data-testid="mfa-secret">{setupData.secret}</code>
              </div>
            </div>
            <div>
              <Label>2. Enter the 6-digit code from your app</Label>
              <div className="flex gap-2">
                <Input data-testid="mfa-verify-code" inputMode="numeric" placeholder="123456" value={code} onChange={(e) => setCode(e.target.value)} />
                <Button data-testid="mfa-enable-btn" disabled={code.length < 6 || enable.isPending} onClick={() => enable.mutate()}>
                  {enable.isPending && <Loader2 className="h-4 w-4 animate-spin" />} Verify & enable
                </Button>
              </div>
            </div>
          </div>
        )}

        {!status.enabled && newCodes && (
          <div className="space-y-3">
            <p className="flex items-center gap-2 text-sm font-medium text-emerald-700">
              <ShieldCheck className="h-4 w-4" /> Two-factor authentication is now enabled.
            </p>
            <BackupCodes codes={newCodes} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
